import { Component, HostListener, OnInit, OnDestroy, ViewChild, TemplateRef, Input, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MessageService } from '../../../core/service/message.service';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs';
import { FileService } from '../../../core/service/file.service';
import { ProfileManagementService } from '../../service/data/profile-management.service';
import { UserAccountService } from '../../service/user-account.service';
import { LanguageSelectorType } from '../language-select/language-select.component';
import { SidebarService } from '../../../core/service/sidebar.service';
import { LicenseService } from '../../service/data/license.service';
import { LicenseLimitComparison } from '../../model/license';

@Component({
  selector: 'app-user-account-menu',
  templateUrl: './user-account-menu.component.html',
  styleUrls: ['./user-account-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserAccountMenuComponent implements OnInit, OnDestroy {
  private _isAdmin?: boolean;

  constructor(
    private readonly _fileService: FileService,
    private readonly _message: MessageService,
    private readonly _profile: ProfileManagementService,
    private readonly _router: Router,
    private readonly _sidebarService: SidebarService,
    private readonly _userService: UserAccountService,
    private readonly _licenseService: LicenseService
  ) {}

  private userSubscription: Subscription = new Subscription();

  email: string = '';
  logoFileUrl: string = '';
  name: string = '';
  showMenu: boolean = false;
  cancelHide: boolean = false;
  languageSelectorType: LanguageSelectorType = LanguageSelectorType.SLIDER;
  dropdownOpen: boolean = false;

  @Input() set isAdmin(value: boolean | undefined) {
    this._isAdmin = value;
    if (value) {
      this.reloadLicenseLimitsFromApi();
    }
  }

  get isAdmin(): boolean | undefined {
    return this._isAdmin;
  }

  limitsComparison: LicenseLimitComparison;
  breachedLimits: { [key: string]: boolean } = {
    dwelling: false,
    parking: false,
    otherObject: false,
    premisesSqm: false,
  };

  get hasBreachedLimits(): boolean {
    return (
      this.breachedLimits.dwelling || this.breachedLimits.parking || this.breachedLimits.otherObject || this.breachedLimits.premisesSqm
    );
  }

  // For logo editing
  private _editLogo: boolean = false;

  @ViewChild('licenseSidebar', { static: false }) licenseSidebarContent!: TemplateRef<any>;

  @HostListener('mouseleave') onMouseLeave(): void {
    if (!this._editLogo) {
      this.cancelHide = false;
      setTimeout(() => {
        if (!this.cancelHide) {
          this.showMenu = false;
        }
      }, 200);
    }
  }

  @HostListener('mouseenter') onMouseEnter() {
    this.cancelHide = true;
  }

  onOpenLicenseSidebar() {
    this.reloadLicenseLimitsFromApi();

    this._sidebarService.setSidebarContent({
      template: this.licenseSidebarContent,
      onClose: () => {},
    });
    this._sidebarService.toggleSidebar(true);
  }

  reloadLicenseLimitsFromApi() {
    if (!this.isAdmin) {
      return;
    }

    this.limitsComparison = undefined;
    this.breachedLimits = {
      dwelling: false,
      parking: false,
      otherObject: false,
      premisesSqm: false,
    };

    this._licenseService
      .getLimitsComparison()
      .pipe(take(1))
      .subscribe(data => {
        this.applyLimitsComparison(new LicenseLimitComparison(data));
      });
  }

  private applyLimitsComparison(limitsComparison: LicenseLimitComparison) {
    this.limitsComparison = limitsComparison;
    this.breachedLimits = {
      dwelling: false,
      parking: false,
      otherObject: false,
      premisesSqm: false,
    };

    if (this.limitsComparison.dwellingLimit !== undefined && this.limitsComparison.dwellingLevel > this.limitsComparison.dwellingLimit) {
      this.breachedLimits.dwelling = true;
    }
    if (this.limitsComparison.parkingLimit !== undefined && this.limitsComparison.parkingLevel > this.limitsComparison.parkingLimit) {
      this.breachedLimits.parking = true;
    }
    if (
      this.limitsComparison.otherObjectLimit !== undefined &&
      this.limitsComparison.otherObjectLevel > this.limitsComparison.otherObjectLimit
    ) {
      this.breachedLimits.otherObject = true;
    }
    if (
      this.limitsComparison.premisesSqmLimit !== undefined &&
      this.limitsComparison.premisesSqmLevel > this.limitsComparison.premisesSqmLimit
    ) {
      this.breachedLimits.premisesSqm = true;
    }
  }

  ngOnInit() {
    // User subscription
    this.userSubscription.add(
      this._userService.current.subscribe(x => {
        this.email = x.email;
        this.name = x.name;
      })
    );

    // Logo subscription
    this.userSubscription.add(this._profile.getPhoto().subscribe(res => (this.logoFileUrl = res)));

    this.userSubscription.add(
      this._router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        if (this.isAdmin) {
          this.reloadLicenseLimitsFromApi();
        }
      })
    );
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
  }

  logout() {
    this._userService.logout();
  }

  close() {
    this.showMenu = false;
  }

  onBtnClick() {
    this._editLogo = false;
    this.showMenu = !this.showMenu;
  }

  onChangeIcon(files: FileList) {
    this.userSubscription.add(
      this._fileService.getImage(files).subscribe(image => {
        this.logoFileUrl = image.url;
        this.userSubscription.add(
          this._profile.setPhoto(image.source).subscribe(
            () => {},
            error => this._message.showError(error)
          )
        );
      })
    );
  }

  onLogoClick() {
    this._editLogo = true;
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }
}
